import { Injectable, effect, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ChatNotificationsService } from './chat-notifications.service';

/**
 * Dibuja un badge en el favicon con el contador de no-leídos del chat.
 *
 * Implementación: canvas a mano (sin librería externa). Cada vez que cambia
 * `totalNoLeidos`, redibujamos el favicon como composición:
 *   1. Cargamos el favicon original (vacío) en una `Image`.
 *   2. En un `<canvas>` de 32×32 dibujamos el icon original.
 *   3. Encima dibujamos un círculo rojo con el número (99+ si excede 99).
 *   4. Convertimos a data-URL y lo seteamos en el `<link rel="icon">`.
 *
 * Si el contador vuelve a 0, restauramos el favicon original.
 *
 * Compat: funciona en Chrome/Edge/Firefox. Safari ignora favicons dinámicos
 * silenciosamente — no se rompe, simplemente no muestra el badge.
 */
@Injectable({ providedIn: 'root' })
export class FaviconBadgeService {
  private notifs = inject(ChatNotificationsService);
  private doc = inject(DOCUMENT);

  /** URL del favicon original cargado al iniciar — para poder restaurar. */
  private originalHref: string | null = null;
  /** Imagen del favicon original ya cargada (cache). */
  private originalImage: HTMLImageElement | null = null;

  constructor() {
    this.captureOriginal();

    effect(() => {
      const total = this.notifs.totalNoLeidos();
      void this.render(total);
    });
  }

  private captureOriginal(): void {
    const link = this.getOrCreateLink();
    this.originalHref = link.href || '/favicon.ico';

    // Pre-cargamos la imagen para que el primer render del badge sea rápido.
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { this.originalImage = img; };
    img.onerror = () => {
      // El favicon puede ser .ico que el browser sí soporta como `<link>` pero
      // no necesariamente desde `Image` (algunos no decodifican .ico). En ese
      // caso quedamos sin imagen base y dibujamos solo el badge sobre fondo.
      this.originalImage = null;
    };
    img.src = this.originalHref;
  }

  private async render(total: number): Promise<void> {
    if (total <= 0) {
      this.restore();
      return;
    }

    const canvas = this.doc.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 1. Icon base (si lo tenemos).
    if (this.originalImage) {
      ctx.drawImage(this.originalImage, 0, 0, 32, 32);
    } else {
      // Fallback: cuadrado redondeado con primera letra "C" (de Crm/Chat).
      ctx.fillStyle = '#1890ff';
      this.roundedRect(ctx, 0, 0, 32, 32, 6);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 18px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('C', 16, 16);
    }

    // 2. Badge rojo: círculo (o pill si "99+") en la esquina inferior derecha.
    const display = total > 99 ? '99+' : String(total);
    const isWide = display.length >= 2;
    const badgeW = isWide ? 22 : 16;
    const badgeH = 16;
    const badgeX = 32 - badgeW;
    const badgeY = 32 - badgeH;

    ctx.fillStyle = '#ef4444'; // rojo Tailwind 500
    this.roundedRect(ctx, badgeX, badgeY, badgeW, badgeH, 8);
    ctx.fill();

    // Borde blanco para contraste contra cualquier icono base.
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = '#fff';
    this.roundedRect(ctx, badgeX, badgeY, badgeW, badgeH, 8);
    ctx.stroke();

    // 3. Número.
    ctx.fillStyle = '#fff';
    ctx.font = `bold ${isWide ? 10 : 11}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(display, badgeX + badgeW / 2, badgeY + badgeH / 2 + 0.5);

    // 4. Aplicar al <link>.
    this.setFavicon(canvas.toDataURL('image/png'));
  }

  private restore(): void {
    if (this.originalHref) this.setFavicon(this.originalHref);
  }

  private setFavicon(href: string): void {
    const link = this.getOrCreateLink();
    link.type = 'image/png';
    link.href = href;
  }

  private getOrCreateLink(): HTMLLinkElement {
    let link = this.doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.rel = 'icon';
      this.doc.head.appendChild(link);
    }
    return link;
  }

  private roundedRect(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number,
  ): void {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }
}
